import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ShieldCheckIcon, UsersIcon, StarIcon, CompassIcon } from 'lucide-react';
export function WhyChooseUs() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: i * 0.12,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };
  return <section className="w-full bg-[#FAF8F5] py-24 sm:py-32" ref={ref} data-id="element-421">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-422">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-423">
          <motion.div initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex items-center justify-center gap-3 mb-6" data-id="element-424">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-425">◆</span>
            <motion.div initial={{
            scaleX: 0
          }} animate={isInView ? {
            scaleX: 1
          } : {}} transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} className="w-16 h-0.5 bg-[#D4A853] origin-center" data-id="element-426" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-427">◆</span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4" data-id="element-428">
            Why Choose Nepal Luxury Travel
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-slate-500 text-lg max-w-xl mx-auto font-light" data-id="element-429">
            We go beyond ordinary to deliver extraordinary experiences
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-id="element-430">
          {/* Feature 1 — Safety First */}
          <motion.div custom={0} variants={itemVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="text-center bg-white rounded-2xl p-8 shadow-sm border border-[#D4A853]/10" data-id="element-431">
            <div className="w-16 h-16 bg-[#D4A853]/10 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-432">
              <ShieldCheckIcon className="w-7 h-7 text-teal-600" data-id="element-433" />
            </div>
            <h3 className="font-playfair text-xl font-semibold text-slate-900" data-id="element-434">
              Safety First
            </h3>
            <div className="w-8 h-px bg-[#D4A853]/30 mx-auto mt-2 mb-3" data-id="element-435" />
            <p className="text-slate-500 text-sm leading-relaxed" data-id="element-436">
              Comprehensive safety protocols, satellite communication, emergency
              evacuation plans, and fully insured operations on every journey.
            </p>
          </motion.div>

          {/* Feature 2 — Expert Local Guides */}
          <motion.div custom={1} variants={itemVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="text-center bg-white rounded-2xl p-8 shadow-sm border border-[#D4A853]/10" data-id="element-437">
            <div className="w-16 h-16 bg-[#D4A853]/10 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-438">
              <UsersIcon className="w-7 h-7 text-teal-600" data-id="element-439" />
            </div>
            <h3 className="font-playfair text-xl font-semibold text-slate-900" data-id="element-440">
              Expert Local Guides
            </h3>
            <div className="w-8 h-px bg-[#D4A853]/30 mx-auto mt-2 mb-3" data-id="element-441" />
            <p className="text-slate-500 text-sm leading-relaxed" data-id="element-442">
              Certified, multilingual guides with decades of experience who
              share deep cultural knowledge and ensure authentic encounters.
            </p>
          </motion.div>

          {/* Feature 3 — Luxury Accommodations */}
          <motion.div custom={2} variants={itemVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="text-center bg-white rounded-2xl p-8 shadow-sm border border-[#D4A853]/10" data-id="element-443">
            <div className="w-16 h-16 bg-[#D4A853]/10 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-444">
              <StarIcon className="w-7 h-7 text-teal-600" data-id="element-445" />
            </div>
            <h3 className="font-playfair text-xl font-semibold text-slate-900" data-id="element-446">
              Luxury Accommodations
            </h3>
            <div className="w-8 h-px bg-[#D4A853]/30 mx-auto mt-2 mb-3" data-id="element-447" />
            <p className="text-slate-500 text-sm leading-relaxed" data-id="element-448">
              Hand-selected five-star hotels, boutique lodges, and premium
              glamping sites that blend comfort with Nepal's natural beauty.
            </p>
          </motion.div>

          {/* Feature 4 — Tailored Experiences */}
          <motion.div custom={3} variants={itemVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="text-center bg-white rounded-2xl p-8 shadow-sm border border-[#D4A853]/10" data-id="element-449">
            <div className="w-16 h-16 bg-[#D4A853]/10 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-450">
              <CompassIcon className="w-7 h-7 text-teal-600" data-id="element-451" />
            </div>
            <h3 className="font-playfair text-xl font-semibold text-slate-900" data-id="element-452">
              Tailored Experiences
            </h3>
            <div className="w-8 h-px bg-[#D4A853]/30 mx-auto mt-2 mb-3" data-id="element-453" />
            <p className="text-slate-500 text-sm leading-relaxed" data-id="element-454">
              Every itinerary is fully customizable — from private helicopter
              tours to exclusive monastery visits and bespoke dining
              experiences.
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}